import React, { useState, useEffect } from 'react';
import { getQuoteLineRemarks, updateQuoteLineRemarks } from '../api';
import useModalBehavior from '../hooks/useModalBehavior';
import './RemarksModal.css';

const RemarksModal = ({ line, isOpen, onClose, onSave }) => {
  const [remarks, setRemarks] = useState('');
  const [originalRemarks, setOriginalRemarks] = useState('');
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);

  const panelRef = useModalBehavior({ isOpen, onClose });

  // Load remarks when modal opens
  useEffect(() => {
    if (isOpen && line) {
      loadRemarks();
    }
  }, [isOpen, line]);

  const loadRemarks = async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await getQuoteLineRemarks(line.id);
      const loadedRemarks = response.data.remarks || '';
      setRemarks(loadedRemarks);
      setOriginalRemarks(loadedRemarks);
    } catch (err) {
      console.error('Error loading remarks:', err);
      setError('Failed to load remarks');
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    setSaving(true);
    setError(null);
    try {
      await updateQuoteLineRemarks(line.id, remarks);
      setOriginalRemarks(remarks);
      if (onSave) {
        onSave(remarks);
      }
      onClose();
    } catch (err) {
      console.error('Error saving remarks:', err);
      setError('Failed to save remarks');
    } finally {
      setSaving(false);
    }
  };

  const handleAutoSave = async () => {
    // Only auto-save if remarks have changed
    if (remarks === originalRemarks) {
      return;
    }

    setSaving(true);
    setError(null);
    try {
      await updateQuoteLineRemarks(line.id, remarks);
      setOriginalRemarks(remarks);
      console.log('Remarks auto-saved');
    } catch (err) {
      console.error('Error auto-saving remarks:', err);
      setError('Failed to auto-save remarks');
    } finally {
      setSaving(false);
    }
  };

  const handleCancel = () => {
    onClose();
  };

  if (!isOpen) {
    return null;
  }

  return (
    <div className="remarks-modal-overlay" onClick={handleCancel}>
      <div
        className="remarks-modal"
        onClick={(e) => e.stopPropagation()}
        ref={panelRef}
        tabIndex={-1}
        role="dialog"
        aria-modal="true"
        aria-labelledby="remarks-modal-title"
      >
        <div className="remarks-modal-header">
          <h3 id="remarks-modal-title">Remarks for {line?.name || 'Quote Line'}</h3>
          <button className="close-button" onClick={handleCancel} aria-label="Close">
            ✕
          </button>
        </div>

        <div className="remarks-modal-body">
          {loading ? (
            <div className="remarks-loading">Loading remarks...</div>
          ) : (
            <>
              <textarea
                className="remarks-textarea"
                value={remarks}
                onChange={(e) => setRemarks(e.target.value)}
                onBlur={handleAutoSave}
                placeholder="Add remarks for this quote line..."
                rows={10}
                disabled={saving}
              />
              {saving && <div className="remarks-saving">Saving...</div>}
              {error && <div className="remarks-error">{error}</div>}
            </>
          )}
        </div>

        <div className="remarks-modal-footer">
          <button
            className="btn btn-secondary"
            onClick={handleCancel}
            disabled={saving}
          >
            Cancel
          </button>
          <button
            className="btn btn-primary"
            onClick={handleSave}
            disabled={saving || loading}
          >
            {saving ? 'Saving...' : 'Save Remarks'}
          </button>
        </div>
      </div>
    </div>
  );
};

export default RemarksModal;
